import type { OrgEnablement, PartnerClient } from '@epilot/sdk/identity'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { OrganizationForm } from './organization-form'
import { ConfirmAction, ErrorNotice, StatusBadge } from './shared'

import { getIdentityClient } from '@/api/identity-client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  useOidcOrganizations,
  useRefreshIdentity
} from '@/services/identity.service'

type Action = {
  kind: 'activate' | 'suspend' | 'revoke'
  organization: OrgEnablement
}

export const ClientOrganizations = ({ client }: { client: PartnerClient }) => {
  const query = useOidcOrganizations(client.client_id)
  const refresh = useRefreshIdentity()
  const [form, setForm] = useState<{ organization?: OrgEnablement }>()
  const [action, setAction] = useState<Action>()
  const retired = client.status === 'retired'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Only enabled organizations can use this application. The application
          must also be active.
        </p>
        <Button disabled={retired} onClick={() => setForm({})}>
          Enable organization
        </Button>
      </div>
      <ErrorNotice message={query.error?.message} />
      {query.isPending ? (
        <p role="status">Loading organizations…</p>
      ) : (
        <Table className="text-primary">
          <TableHeader>
            <TableRow>
              <TableHead>Organization</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Approval</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data?.map((org) => (
              <TableRow key={org.org_id}>
                <TableCell>
                  <Link
                    className="underline"
                    to={`/organizations/${encodeURIComponent(org.org_id)}`}
                  >
                    {org.org_id}
                  </Link>
                </TableCell>
                <TableCell>
                  <StatusBadge status={org.status} />
                  {org.status_reason && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {org.status_reason}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  {org.authorization_policy.allowed_scopes.join(', ')}
                </TableCell>
                <TableCell>
                  <p>{org.authorization_policy.customer_approval_ref}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(
                      org.authorization_policy.approved_at
                    ).toLocaleString()}{' '}
                    · v{org.authorization_policy.version}
                  </p>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      disabled={retired}
                      onClick={() => setForm({ organization: org })}
                      size="sm"
                      variant="outline"
                    >
                      {org.status === 'revoked'
                        ? 'Enable again'
                        : 'Edit policy'}
                    </Button>
                    {org.status === 'suspended' && (
                      <Button
                        disabled={retired}
                        onClick={() =>
                          setAction({ kind: 'activate', organization: org })
                        }
                        size="sm"
                      >
                        Activate
                      </Button>
                    )}
                    {org.status === 'active' && (
                      <Button
                        disabled={retired}
                        onClick={() =>
                          setAction({ kind: 'suspend', organization: org })
                        }
                        size="sm"
                        variant="outline"
                      >
                        Suspend
                      </Button>
                    )}
                    {org.status !== 'revoked' && (
                      <Button
                        disabled={retired}
                        onClick={() =>
                          setAction({ kind: 'revoke', organization: org })
                        }
                        size="sm"
                        variant="destructive"
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!query.error && !query.data?.length && (
              <TableRow>
                <TableCell
                  className="text-center text-muted-foreground"
                  colSpan={5}
                >
                  No organizations enabled.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      {form && (
        <Dialog
          onOpenChange={(open) => {
            if (!open) setForm(undefined)
          }}
          open
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto text-primary">
            <DialogHeader>
              <DialogTitle className="text-primary">
                {form.organization && form.organization.status !== 'revoked'
                  ? 'Edit organization policy'
                  : 'Enable organization'}
              </DialogTitle>
              <DialogDescription>{client.display_name}</DialogDescription>
            </DialogHeader>
            <OrganizationForm
              client={client}
              onSaved={() => setForm(undefined)}
              organization={form.organization}
            />
          </DialogContent>
        </Dialog>
      )}
      {action && (
        <ConfirmAction
          askReason={action.kind !== 'activate'}
          description={
            action.kind === 'activate'
              ? `Resume sign-in for organization ${action.organization.org_id}.`
              : `${
                  action.kind === 'revoke' ? 'Revoke' : 'Suspend'
                } organization ${
                  action.organization.org_id
                } on this application. Future sign-ins are blocked and provider tokens are revoked. Existing partner sessions may continue until the partner ends them.`
          }
          destructive={action.kind !== 'activate'}
          onClose={() => setAction(undefined)}
          onConfirm={async (reason) => {
            const identity = getIdentityClient()
            const target = {
              client_id: client.client_id,
              org_id: action.organization.org_id
            }

            if (action.kind === 'activate')
              await identity.activateClientOrganization(target)
            else if (action.kind === 'suspend')
              await identity.suspendClientOrganization(target, { reason })
            else await identity.revokeClientOrganization(target, { reason })
            void refresh()
          }}
          title={`${
            action.kind === 'activate'
              ? 'Activate'
              : action.kind === 'suspend'
              ? 'Suspend'
              : 'Revoke'
          } organization`}
        />
      )}
    </div>
  )
}
